/*
  dylaras-product-gallery.js
  Product detail gallery enhancements:
  1. Hover zoom (desktop / fine pointer only). The zoom level follows the largest image in the
     srcset, so low-resolution photos are never upscaled. Click still opens the theme zoom dialog.
  2. Previous / next buttons for the thumbnail column when it has more photos than fit.
*/
(() => {
  const MAX_SCALE = 2.5;
  const MIN_SCALE = 1.3;
  const CONTAINER = '.product-information media-gallery .product-media-container--zoomable';
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  /** @type {WeakMap<HTMLElement, { lens: HTMLElement, img: HTMLImageElement, scale: number }>} */
  const states = new WeakMap();

  /**
   * Largest candidate from an img srcset ("url 1200w, ...").
   * @param {HTMLImageElement} img
   */
  function largestSource(img) {
    let best = { url: img.currentSrc || img.src, width: img.naturalWidth };
    for (const candidate of (img.srcset || '').split(',')) {
      const [url, descriptor] = candidate.trim().split(/\s+/);
      const width = parseInt(descriptor, 10);
      if (url && width > best.width) best = { url, width };
    }
    return best;
  }

  /** @param {HTMLElement} container */
  function setup(container) {
    if (states.has(container)) return states.get(container);

    const img = container.querySelector('.product-media img');
    if (!(img instanceof HTMLImageElement)) return null;

    const rendered = img.getBoundingClientRect().width;
    const source = largestSource(img);
    if (!rendered) return null;

    const scale = Math.min(MAX_SCALE, source.width / rendered);
    if (scale < MIN_SCALE) {
      // Not enough resolution to zoom: remember it so we don't re-measure on every move
      states.set(container, null);
      return null;
    }

    const lens = document.createElement('div');
    lens.className = 'dylaras-zoom-lens';
    lens.setAttribute('aria-hidden', 'true');
    lens.style.backgroundImage = `url("${source.url}")`;
    container.appendChild(lens);

    const state = { lens, img, scale };
    states.set(container, state);
    return state;
  }

  /** @param {PointerEvent} event */
  function onMove(event) {
    if (!canHover.matches || !(event.target instanceof Element)) return;

    const container = event.target.closest(CONTAINER);
    if (!(container instanceof HTMLElement)) return;

    const state = setup(container);
    if (!state) return;

    const box = state.img.getBoundingClientRect();
    const host = container.getBoundingClientRect();
    const x = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1);
    const y = Math.min(Math.max((event.clientY - box.top) / box.height, 0), 1);

    Object.assign(state.lens.style, {
      left: `${box.left - host.left}px`,
      top: `${box.top - host.top}px`,
      width: `${box.width}px`,
      height: `${box.height}px`,
      backgroundSize: `${box.width * state.scale}px ${box.height * state.scale}px`,
      backgroundPosition: `${x * 100}% ${y * 100}%`,
    });
    container.classList.add('dylaras-zooming');
  }

  /** @param {PointerEvent} event */
  function onLeave(event) {
    if (!(event.target instanceof Element) || !event.target.matches(CONTAINER)) return;
    event.target.classList.remove('dylaras-zooming');
  }

  document.addEventListener('pointermove', onMove, { passive: true });
  document.addEventListener('pointerleave', onLeave, { capture: true, passive: true });

  // Rendered size changes on resize; rebuild lenses lazily on next hover
  window.addEventListener('resize', () => {
    document.querySelectorAll('.dylaras-zoom-lens').forEach((lens) => {
      const container = lens.parentElement;
      lens.remove();
      if (container) states.delete(container);
    });
  });
})();

(() => {
  const CONTAINER = '.product-information media-gallery .slideshow-controls__thumbnails-container';
  const isSpanish = document.documentElement.lang.toLowerCase().startsWith('es');
  const LABELS = isSpanish
    ? { previous: 'Fotos anteriores', next: 'Más fotos' }
    : { previous: 'Previous photos', next: 'More photos' };
  const ICON =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 15l6-6 6 6"/></svg>';

  /** @param {HTMLElement} container */
  const isVertical = (container) => container.scrollHeight > container.scrollWidth;

  /**
   * @param {HTMLElement} container
   * @param {HTMLButtonElement} previous
   * @param {HTMLButtonElement} next
   */
  function update(container, previous, next) {
    const vertical = isVertical(container);
    const position = vertical ? container.scrollTop : container.scrollLeft;
    const max = vertical
      ? container.scrollHeight - container.clientHeight
      : container.scrollWidth - container.clientWidth;
    const overflowing = max > 2;

    container.parentElement?.classList.toggle('dylaras-thumbs--overflowing', overflowing);
    container.parentElement?.classList.toggle('dylaras-thumbs--horizontal', !vertical);
    previous.disabled = !overflowing || position <= 1;
    next.disabled = !overflowing || position >= max - 1;
  }

  /** @param {HTMLElement} container */
  function setup(container) {
    if (container.dataset.dylarasThumbs) return;
    container.dataset.dylarasThumbs = 'true';

    const makeButton = (direction) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `dylaras-thumbs-nav dylaras-thumbs-nav--${direction}`;
      button.setAttribute('aria-label', LABELS[direction]);
      button.innerHTML = ICON;
      button.addEventListener('click', () => {
        const vertical = isVertical(container);
        const page = (vertical ? container.clientHeight : container.clientWidth) * 0.8;
        const delta = direction === 'next' ? page : -page;
        container.scrollBy({ [vertical ? 'top' : 'left']: delta, behavior: 'smooth' });
      });
      return button;
    };

    const previous = makeButton('previous');
    const next = makeButton('next');
    container.before(previous);
    container.after(next);

    const refresh = () => update(container, previous, next);
    container.addEventListener('scroll', refresh, { passive: true });
    new ResizeObserver(refresh).observe(container);
    refresh();
  }

  const init = () => document.querySelectorAll(CONTAINER).forEach((el) => setup(/** @type {HTMLElement} */ (el)));

  init();
  // The product section re-renders on variant change; pick up the new gallery markup
  let pending = 0;
  new MutationObserver(() => {
    cancelAnimationFrame(pending);
    pending = requestAnimationFrame(init);
  }).observe(document.body, { childList: true, subtree: true });
})();
